import { useState } from 'react';
import { useRowboat } from '@/rowboat';
import type { FetchedPhoto } from '@/services/exportService';
import { fetchPhotoBytes } from '@/services/fetchPhoto';

async function fetchViaBlobClient(hash: string): Promise<FetchedPhoto> {
  return fetchPhotoBytes(hash);
}

export function ExportPanel({
  estateId,
  estateName,
  fetchPhoto = fetchViaBlobClient,
}: {
  estateId: string;
  estateName: string;
  fetchPhoto?: (hash: string) => Promise<FetchedPhoto>;
}) {
  const graph = useRowboat();
  const [progress, setProgress] = useState('');
  const [summary, setSummary] = useState('');
  const [error, setError] = useState('');

  const run = async () => {
    setError('');
    setSummary('');
    setProgress('Collecting items…');
    try {
      const { downloadEstateZip } = await import('@/services/exportService');
      const { summary } = await downloadEstateZip(
        graph,
        estateId,
        estateName,
        fetchPhoto,
        (done, total) => setProgress(`Exporting photos (${done}/${total})…`),
      );
      setProgress('');
      setSummary(summary);
    } catch (e) {
      setProgress('');
      setError(e instanceof Error ? e.message : 'Export failed');
    }
  };

  return (
    <section
      aria-label="Export"
      className="rounded-2xl border border-divider bg-surface-elevated p-4 shadow-sm"
    >
      <h3 className="mb-2 text-base font-semibold text-content-primary">Export</h3>
      <button
        type="button"
        onClick={() => void run()}
        disabled={!estateId || progress !== ''}
        className="inline-flex min-h-11 items-center rounded-xl bg-primary-600 px-5 py-2.5 text-base font-medium text-white transition hover:bg-primary-700 active:scale-[0.98] disabled:opacity-50"
      >
        Download estate zip
      </button>
      {progress && <p className="mt-2 text-sm text-content-secondary">{progress}</p>}
      {summary && <p className="mt-2 text-sm text-content-secondary">{summary}</p>}
      {error && <p role="alert">{error}</p>}
    </section>
  );
}
